import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Phone, Clock, Check, ArrowLeft, Loader2, CalendarDays, ShieldCheck } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SiteFooter from '../components/SiteFooter';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const field = "w-full px-4 py-3 border border-sky-gray/30 rounded-xl focus:ring-2 focus:ring-sky-gray focus:border-sky-gray outline-none text-rabbit";
const labelCls = "block text-sm font-semibold text-rabbit mb-1.5";

const money = (n) => `£${Number(n).toFixed(0)}`;

const Step = ({ n, current, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${current >= n ? 'bg-burnt-coral text-white' : 'bg-sky-gray/15 text-rabbit/60'}`}>
      {current > n ? <Check className="w-4 h-4" /> : n}
    </div>
    <span className={`hidden sm:inline text-sm font-medium ${current >= n ? 'text-rabbit' : 'text-rabbit/50'}`}>{label}</span>
  </div>
);

export default function Book() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [avail, setAvail] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [activeDay, setActiveDay] = useState(0);
  const [slot, setSlot] = useState(null);
  const [patient, setPatient] = useState({ title: 'Mr', first_name: '', last_name: '', date_of_birth: '', gender: 'female', email: '', phone: '' });
  const [signature, setSignature] = useState('');
  const sigRef = useRef(null);
  const sigWrapRef = useRef(null);
  const [hasSig, setHasSig] = useState(false);
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/booking/config`).then(r => r.json()).then(setConfig).catch(() => setError('Could not load booking options.'));
  }, []);

  // Size the signature canvas to its container (keeps finger/mouse coordinates accurate)
  useEffect(() => {
    if (step !== 4) return undefined;
    const resize = () => {
      const c = sigRef.current && sigRef.current.getCanvas();
      const wrap = sigWrapRef.current;
      if (c && wrap) {
        c.width = wrap.clientWidth;
        c.height = 170;
        if (sigRef.current) sigRef.current.clear();
        setHasSig(false);
      }
    };
    const id = setTimeout(resize, 0);
    window.addEventListener('resize', resize);
    return () => { clearTimeout(id); window.removeEventListener('resize', resize); };
  }, [step]);

  const chooseType = async (t) => {
    setType(t); setSlot(null); setAvail(null); setActiveDay(0); setLoadingAvail(true); setStep(2);
    try {
      const r = await fetch(`${API}/api/booking/availability?type_id=${t.id}`);
      const d = await r.json();
      setAvail(d);
    } catch { setError('Could not load availability.'); }
    setLoadingAvail(false);
  };

  const fmtSlot = (iso) => new Date(iso).toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });

  const detailsValid = patient.first_name.trim() && patient.last_name.trim() && patient.email.trim() && patient.phone.trim() && patient.date_of_birth;

  const pay = async () => {
    setError(''); setSubmitting(true);
    const signature_image = sigRef.current && !sigRef.current.isEmpty() ? sigRef.current.toDataURL('image/png') : '';
    if (!signature_image) { setError('Please draw your signature in the box to accept the policy.'); setSubmitting(false); return; }
    try {
      const r = await fetch(`${API}/api/booking/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type_id: type.id, start_time: slot, patient, signature_name: signature, signature_image, accept_policy: accept, origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.detail || 'Could not start checkout.'); setSubmitting(false); return; }
      window.location.href = d.checkout_url;
    } catch {
      setError('Something went wrong. Please try again.'); setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Book an appointment | Restore.Dental" description="Book a consultation with Dr Kartik Patel at Restore.Dental in Acton, London. Choose a time, confirm with secure payment." path="/book" />
      <PageHeader />
      <main className="min-h-screen bg-gradient-to-br from-sky-gray/5 to-white pt-28 pb-20 px-4" data-testid="book-page">
        <div className="container mx-auto max-w-3xl">
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-rabbit mb-3">Book your appointment</h1>
            <p className="text-chocolate-malt/80 text-base">with {config?.practitioner_name || 'Dr Kartik Patel'} · 64a Horn Lane, Acton, London W3 6NP</p>
          </div>

          <div className="flex items-center justify-between gap-2 mb-8 bg-white rounded-2xl border border-sky-gray/20 px-5 py-4">
            <Step n={1} current={step} label="Appointment" />
            <div className="flex-1 h-px bg-sky-gray/20 mx-1" />
            <Step n={2} current={step} label="Time" />
            <div className="flex-1 h-px bg-sky-gray/20 mx-1" />
            <Step n={3} current={step} label="Your details" />
            <div className="flex-1 h-px bg-sky-gray/20 mx-1" />
            <Step n={4} current={step} label="Confirm & pay" />
          </div>

          {error && <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" data-testid="book-error">{error}</div>}

          {/* STEP 1 — appointment type */}
          {step === 1 && (
            <div className="space-y-4" data-testid="book-step-type">
              {!config && <div className="text-rabbit/60 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
              {config?.appointment_types?.map((t) => (
                <button key={t.id} onClick={() => chooseType(t)} data-testid={`book-type-${t.id}`}
                  className="w-full text-left bg-white rounded-2xl border border-sky-gray/20 hover:border-burnt-coral hover:shadow-md transition-all p-5 flex items-start justify-between gap-4 group">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold text-rabbit">{t.name}</h3>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-rabbit/60"><Clock className="w-3.5 h-3.5" />{t.duration_minutes} min</span>
                    </div>
                    <p className="text-sm text-chocolate-malt/75 leading-relaxed">{t.description}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-2xl font-bold text-burnt-coral">{money(t.fee_gbp)}</div>
                    <div className="text-xs text-rabbit/50">paid to confirm</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* STEP 2 — time */}
          {step === 2 && (
            <div data-testid="book-step-time">
              <button onClick={() => setStep(1)} className="inline-flex items-center gap-1 text-sm text-burnt-coral mb-4" data-testid="book-back-to-type"><ArrowLeft className="w-4 h-4" /> Change appointment</button>
              <div className="bg-white rounded-2xl border border-sky-gray/20 p-5">
                <div className="flex items-center gap-2 mb-4"><CalendarDays className="w-5 h-5 text-sky-gray" /><h3 className="font-bold text-rabbit">{type?.name} — pick a time</h3></div>
                {loadingAvail && <div className="text-rabbit/60 flex items-center gap-2 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading Dr Patel's live diary…</div>}
                {!loadingAvail && avail && avail.days.length === 0 && <p className="text-rabbit/60 py-6">No online slots in the next few weeks. Please call us on 07787 814 064.</p>}
                {!loadingAvail && avail && avail.days.length > 0 && (
                  <>
                    <div className="flex gap-2 overflow-x-auto pb-3 mb-4 border-b border-sky-gray/10">
                      {avail.days.map((d, i) => (
                        <button key={d.date} onClick={() => setActiveDay(i)} data-testid={`book-day-${i}`}
                          className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${activeDay === i ? 'bg-burnt-coral text-white' : 'bg-sky-gray/10 text-rabbit hover:bg-sky-gray/20'}`}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {avail.days[activeDay].slots.map((s) => (
                        <button key={s.start} onClick={() => { setSlot(s.start); setStep(3); }} data-testid={`book-slot-${s.time}`}
                          className="px-3 py-2.5 rounded-lg border border-sky-gray/30 text-rabbit font-medium hover:bg-burnt-coral hover:text-white hover:border-burnt-coral transition-colors">
                          {s.time}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* STEP 3 — details */}
          {step === 3 && (
            <div data-testid="book-step-details">
              <button onClick={() => setStep(2)} className="inline-flex items-center gap-1 text-sm text-burnt-coral mb-4" data-testid="book-back-to-time"><ArrowLeft className="w-4 h-4" /> Change time</button>
              <div className="bg-white rounded-2xl border border-sky-gray/20 p-5">
                <div className="mb-5 p-3 rounded-xl bg-sky-gray/10 text-sm text-rabbit"><strong>{type?.name}</strong> · {slot && fmtSlot(slot)} · {money(type?.fee_gbp)}</div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                    <label className={labelCls}>Title</label>
                    <select className={field} value={patient.title} onChange={e => setPatient({ ...patient, title: e.target.value })} data-testid="book-title">
                      {['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Mx'].map(x => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Gender <span className="text-rabbit/40 font-normal">(for records)</span></label>
                    <select className={field} value={patient.gender} onChange={e => setPatient({ ...patient, gender: e.target.value })} data-testid="book-gender">
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                    </select>
                  </div>
                  <div><label className={labelCls}>First name</label><input className={field} value={patient.first_name} onChange={e => setPatient({ ...patient, first_name: e.target.value })} data-testid="book-first-name" /></div>
                  <div><label className={labelCls}>Last name</label><input className={field} value={patient.last_name} onChange={e => setPatient({ ...patient, last_name: e.target.value })} data-testid="book-last-name" /></div>
                  <div><label className={labelCls}>Email</label><input type="email" className={field} value={patient.email} onChange={e => setPatient({ ...patient, email: e.target.value })} data-testid="book-email" /></div>
                  <div><label className={labelCls}>Phone</label><input className={field} value={patient.phone} onChange={e => setPatient({ ...patient, phone: e.target.value })} data-testid="book-phone" /></div>
                  <div className="col-span-2"><label className={labelCls}>Date of birth</label><input type="date" className={field} value={patient.date_of_birth} onChange={e => setPatient({ ...patient, date_of_birth: e.target.value })} data-testid="book-dob" /></div>
                </div>
                <Button size="lg" className="w-full mt-6 bg-burnt-coral hover:bg-burnt-coral/90 text-white" disabled={!detailsValid} onClick={() => setStep(4)} data-testid="book-to-confirm">Continue</Button>
              </div>
            </div>
          )}

          {/* STEP 4 — policy + pay */}
          {step === 4 && (
            <div data-testid="book-step-confirm">
              <button onClick={() => setStep(3)} className="inline-flex items-center gap-1 text-sm text-burnt-coral mb-4" data-testid="book-back-to-details"><ArrowLeft className="w-4 h-4" /> Edit details</button>
              <div className="bg-white rounded-2xl border border-sky-gray/20 p-5 space-y-5">
                <div className="p-4 rounded-xl bg-sky-gray/10">
                  <div className="font-bold text-rabbit">{type?.name}</div>
                  <div className="text-sm text-chocolate-malt/80 mt-1">{slot && fmtSlot(slot)}</div>
                  <div className="text-sm text-chocolate-malt/80">{patient.title} {patient.first_name} {patient.last_name} · {patient.email}</div>
                  <div className="mt-2 text-lg font-bold text-burnt-coral">{money(type?.fee_gbp)} payable now to confirm</div>
                </div>

                <div>
                  <h3 className="font-bold text-rabbit mb-2 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-sky-gray" /> Cancellation policy</h3>
                  <div className="max-h-44 overflow-y-auto text-sm text-chocolate-malt/80 leading-relaxed whitespace-pre-line border border-sky-gray/20 rounded-xl p-4 bg-sky-gray/5" data-testid="book-policy-text">
                    {config?.cancellation_policy}
                  </div>
                  <a href="/cancellation-policy" target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs text-burnt-coral underline" data-testid="book-policy-fulllink">Open the full policy in a new tab</a>
                </div>

                <div>
                  <label className={labelCls}>Type your full name</label>
                  <input className={field} value={signature} onChange={e => setSignature(e.target.value)} placeholder="Your full name" data-testid="book-signature" />
                </div>

                <div>
                  <label className={labelCls}>Sign here with your finger, stylus or mouse</label>
                  <div ref={sigWrapRef} className="relative border border-sky-gray/30 rounded-xl bg-white overflow-hidden">
                    <SignatureCanvas
                      ref={sigRef}
                      penColor="#1f2a2e"
                      canvasProps={{ className: 'block touch-none w-full', 'data-testid': 'book-signature-pad' }}
                      onEnd={() => setHasSig(!!(sigRef.current && !sigRef.current.isEmpty()))}
                    />
                    <button type="button" onClick={() => { if (sigRef.current) sigRef.current.clear(); setHasSig(false); }}
                      className="absolute top-2 right-2 text-xs font-medium text-burnt-coral bg-white/90 border border-sky-gray/20 px-2 py-1 rounded" data-testid="book-signature-clear">Clear</button>
                  </div>
                  <p className="text-xs text-rabbit/50 mt-1">Your signature is saved to a signed copy of this policy for your records.</p>
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="mt-1 w-5 h-5 accent-burnt-coral" checked={accept} onChange={e => setAccept(e.target.checked)} data-testid="book-accept-policy" />
                  <span className="text-sm text-rabbit">I have read and accept the cancellation policy, and I understand the full fee is payable now to confirm my appointment.</span>
                </label>

                <Button size="lg" className="w-full bg-burnt-coral hover:bg-burnt-coral/90 text-white" disabled={!accept || signature.trim().length < 2 || !hasSig || submitting} onClick={pay} data-testid="book-pay-btn">
                  {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Redirecting to secure payment…</> : `Pay ${money(type?.fee_gbp)} & confirm`}
                </Button>
                <p className="text-xs text-rabbit/50 text-center">Secure payment by Stripe. No appointment is held until payment is complete.</p>
              </div>
            </div>
          )}

          <div className="mt-8 text-center text-sm text-rabbit/60">
            Prefer to talk to us? <a href="tel:+447787814064" className="text-burnt-coral font-semibold inline-flex items-center gap-1"><Phone className="w-4 h-4" /> 07787 814 064</a>
          </div>
        </div>
      </main>
      <SiteFooter />
    </>
  );
}
